// =============================================================================
// File: frontend/src/components/TwinPatterns.jsx
// Digital Twin Patterns Tab — Cross-domain health correlations
// Patent claims exercised: P-012 C1-C9
// =============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../config';
import CostChip from './CostChip';
import XaiPromptPanel from './XaiPromptPanel';
import { useCreditSpend } from '../contexts/CreditSpendContext';

const LIFECYCLE_BADGES = {
  discovery: { label: 'Discovery', className: 'dt-lifecycle-discovery' },
  active: { label: 'Active', className: 'dt-lifecycle-active' },
  strengthening: { label: 'Strengthening', className: 'dt-lifecycle-strengthening' },
  weakening: { label: 'Weakening', className: 'dt-lifecycle-weakening' },
  archived: { label: 'Archived', className: 'dt-lifecycle-archived' },
};

const DOMAIN_FILTER_OPTIONS = ['all', 'biometric', 'nutritional', 'behavioral', 'pharmaceutical'];

export default function TwinPatterns({ fetchWithAuth }) {
  const { spend } = useCreditSpend();  // #77 twin_pattern_discovery = 1 credit (subtle chip, low-balance confirm)
  const [patterns, setPatterns] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [domainFilter, setDomainFilter] = useState('all');
  const [selectedPattern, setSelectedPattern] = useState(null);
  const [discovering, setDiscovering] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const loadPatterns = useCallback(async () => {
    try {
      setLoading(true);
      const domainParam = domainFilter !== 'all' ? `&domain=${domainFilter}` : '';
      const res = await fetchWithAuth(`${API_BASE}/api/twin/patterns?limit=50${domainParam}`);
      if (res.ok) {
        const data = await res.json();
        setPatterns(data.items || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error('Failed to load patterns:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, domainFilter]);

  useEffect(() => { loadPatterns(); }, [loadPatterns]);

  const handleDiscover = async () => {
    setDiscovering(true);
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/twin/patterns/discover`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lookback_days: 90 }),
      });
      if (res.ok) {
        const result = await res.json();
        setDiscovering(false);
        const msg = result.reason
          ? `Discovery complete: ${result.reason}`
          : `Discovery complete: ${result.patterns_discovered} new pattern${result.patterns_discovered !== 1 ? 's' : ''} found, ${result.patterns_updated || 0} updated (${result.total_comparisons || 0} comparisons evaluated)`;
        alert(msg);
        loadPatterns();
      } else {
        setDiscovering(false);
        const err = await res.json();
        alert(Array.isArray(err.detail) ? err.detail.map(d => d.msg || d).join(', ') : err.detail || 'Discovery failed');
      }
    } catch (err) {
      setDiscovering(false);
      alert('Network error during pattern discovery');
    }
  };

  const handleViewDetail = async (patternId) => {
    try {
      setLoadingDetail(true);
      const res = await fetchWithAuth(`${API_BASE}/api/twin/patterns/${patternId}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedPattern(data);
      }
    } catch (err) {
      console.error('Failed to load pattern detail:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  const correlationBarWidth = (r) => Math.min(Math.abs(r) * 100, 100);
  const correlationColor = (r) => Math.abs(r) > 0.7 ? 'strong' : Math.abs(r) > 0.5 ? 'moderate' : 'mild';

  return (
    <div className="dt-patterns">
      {/* AI Operation Spinner Overlay */}
      {discovering && (
        <div className="dt-aiOverlay">
          <div className="dt-aiOverlay-content">
            <div className="dt-spinner"></div>
            <p>Discovering patterns — analyzing cross-domain correlations...</p>
            <p className="dt-aiOverlay-sub">This may take 30-60 seconds</p>
          </div>
        </div>
      )}
      {loadingDetail && (
        <div className="dt-aiOverlay">
          <div className="dt-aiOverlay-content">
            <div className="dt-spinner"></div>
            <p>Loading pattern details...</p>
          </div>
        </div>
      )}

      {/* Discovery Action */}
      <div className="dt-patternsHeader">
        <div className="dt-patternsHeader-left">
          <h3>Health Patterns ({total})</h3>
          <div className="dt-domainFilter">
            {DOMAIN_FILTER_OPTIONS.map(opt => (
              <button
                key={opt}
                className={`dt-filterBtn ${domainFilter === opt ? 'active' : ''}`}
                onClick={() => setDomainFilter(opt)}
              >
                {opt === 'all' ? 'All' : opt.charAt(0).toUpperCase() + opt.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => spend('twin_pattern_discovery', 'Discover patterns?', handleDiscover)}
          disabled={discovering}
        >
          {discovering ? 'Discovering...' : <>Discover Patterns <CostChip apiType="twin_pattern_discovery" /></>}
        </button>
      </div>

      {/* Patterns Grid */}
      {loading ? (
        <div className="dt-loading"><div className="dt-spinner"></div></div>
      ) : patterns.length === 0 ? (
        <div className="dt-empty">
          <h3>No Patterns Discovered Yet</h3>
          <p>Run pattern discovery to find correlations across your health data domains.
          You need at least 30 days of health data for meaningful patterns.</p>
        </div>
      ) : (
        <div className="dt-patternsGrid">
          {patterns.map(pattern => {
            const lifecycle = LIFECYCLE_BADGES[pattern.lifecycle_state] || LIFECYCLE_BADGES.discovery;
            const corrStr = correlationColor(pattern.correlation);

            return (
              <div
                key={pattern.id}
                className="dt-patternCard"
                onClick={() => handleViewDetail(pattern.id)}
              >
                <div className="dt-patternCard-header">
                  <span className={`dt-lifecycleBadge ${lifecycle.className}`}>
                    {lifecycle.label}
                  </span>
                  <span className="dt-patternCard-lag">
                    {pattern.lag_hours}h lag
                  </span>
                </div>

                <div className="dt-patternCard-metrics">
                  <span className="dt-patternCard-metricA">
                    {pattern.metric_a.replace(/_/g, ' ')}
                  </span>
                  <span className="dt-patternCard-arrow">
                    {pattern.lag_direction === 'a_leads' ? '→' : '←'}
                  </span>
                  <span className="dt-patternCard-metricB">
                    {pattern.metric_b.replace(/_/g, ' ')}
                  </span>
                </div>

                <div className="dt-patternCard-domains">
                  <span className="dt-domainTag">{pattern.domain_a}</span>
                  <span className="dt-domainTag">{pattern.domain_b}</span>
                </div>

                {/* Correlation Bar */}
                <div className="dt-correlationBar">
                  <div
                    className={`dt-correlationBar-fill dt-corr-${corrStr}`}
                    style={{ width: `${correlationBarWidth(pattern.correlation)}%` }}
                  ></div>
                  <span className="dt-correlationBar-label">
                    r = {pattern.correlation.toFixed(3)}
                  </span>
                </div>

                {pattern.narrative && (
                  <p className="dt-patternCard-narrative">{pattern.narrative}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pattern Detail Modal */}
      {selectedPattern && (
        <div className="modal-overlay" onClick={() => setSelectedPattern(null)}>
          <div className="modal-content dt-patternModal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Pattern Detail</h2>
              <button className="modal-close" onClick={() => setSelectedPattern(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="dt-patternDetail">
                <div className="dt-patternDetail-metrics">
                  <h3>
                    {selectedPattern.metric_a.replace(/_/g, ' ')} ({selectedPattern.domain_a})
                    {' '}{selectedPattern.lag_direction === 'a_leads' ? '→' : '←'}{' '}
                    {selectedPattern.metric_b.replace(/_/g, ' ')} ({selectedPattern.domain_b})
                  </h3>
                </div>

                <div className="dt-patternDetail-stats">
                  <div className="dt-stat">
                    <span className="dt-stat-value">{selectedPattern.correlation.toFixed(4)}</span>
                    <span className="dt-stat-label">Correlation (r)</span>
                  </div>
                  <div className="dt-stat">
                    <span className="dt-stat-value">{selectedPattern.lag_hours}h</span>
                    <span className="dt-stat-label">Optimal Lag</span>
                  </div>
                  <div className="dt-stat">
                    <span className="dt-stat-value">{selectedPattern.p_value.toExponential(2)}</span>
                    <span className="dt-stat-label">P-Value</span>
                  </div>
                  <div className="dt-stat">
                    <span className="dt-stat-value">
                      {selectedPattern.holdout_r?.toFixed(4) || '—'}
                    </span>
                    <span className="dt-stat-label">Holdout r</span>
                  </div>
                </div>

                {selectedPattern.narrative && (
                  <div className="dt-aiExplanation">
                    <h4>AI Interpretation</h4>
                    <p>{selectedPattern.narrative}</p>
                  </div>
                )}

                {/* Apple 1.4.1 — a correlation narrative is health information, so it carries its
                    reasoning and citations like every other AI surface. prompt_data is
                    reconstructed server-side from the stored statistics (twin_patterns has no
                    prompt_data column and deriving it beats a migration for data we already hold). */}
                {selectedPattern.prompt_data && (
                  <XaiPromptPanel
                    promptData={selectedPattern.prompt_data}
                    featureName="Health Pattern Discovery"
                  />
                )}

                {selectedPattern.pharma_context && (
                  <div className="dt-pharmaContext">
                    <h4>Pharmaceutical Context</h4>
                    <pre>{JSON.stringify(selectedPattern.pharma_context, null, 2)}</pre>
                  </div>
                )}

                {/* Validation History */}
                {selectedPattern.validations && selectedPattern.validations.length > 0 && (
                  <div className="dt-validationHistory">
                    <h4>Validation History</h4>
                    <div className="dt-validationList">
                      {selectedPattern.validations.map((v, i) => (
                        <div key={i} className={`dt-validationItem ${v.passed ? 'passed' : 'failed'}`}>
                          <span>{new Date(v.validated_at).toLocaleDateString()}</span>
                          <span>r = {v.validation_r.toFixed(4)}</span>
                          <span>n = {v.sample_size}</span>
                          <span className={v.passed ? 'dt-pass' : 'dt-fail'}>
                            {v.passed ? '✓ Passed' : '✗ Failed'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Lifecycle Timeline */}
                <div className="dt-lifecycleTimeline">
                  <h4>Lifecycle</h4>
                  <div className="dt-lifecycleSteps">
                    {['discovery', 'active', 'strengthening', 'weakening', 'archived'].map(state => {
                      const isCurrent = selectedPattern.lifecycle_state === state;
                      const isPast = ['discovery', 'active', 'strengthening', 'weakening', 'archived']
                        .indexOf(selectedPattern.lifecycle_state) >=
                        ['discovery', 'active', 'strengthening', 'weakening', 'archived'].indexOf(state);

                      return (
                        <span
                          key={state}
                          className={`dt-lifecycleStep ${isCurrent ? 'current' : isPast ? 'past' : ''}`}
                        >
                          {state}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
